import React, { useEffect, useState } from "react";
import { Button, Tooltip, message } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import logo from "../../../Assets/Icons/Avatarprofile.svg";
import GenericStatCard from "../../../Components/Cards/GenericStatCard";
import fleetIcon from "../../../Assets/Icons/laptop_car_fleet.svg";
import vendorIcon from "../../../Assets/Icons/storefront_req.svg";
import approvedIcon from "../../../Assets/Icons/verified.svg";
import creditIcon from "../../../Assets/Icons/credit_card_req.svg";
import GenericTable from "../../../Components/Tables/GenericTable";
import { useNavigate } from "react-router-dom";
import {
  getApprovalsRequests,
  updateTransactionAdjustmentStatus,
} from "../../../API/Admin/Approval&Requests";
import "./style.css";
import usePageAccess from "../../../Utlis/usePageAccess";

const AVATAR_COLORS = ["orange", "blue", "red", "green"];

const countValue = (field) =>
  field && typeof field === "object" ? field.value : field;

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const ROUTES_BY_TYPE = {
  fleet_company: "/company-onboarding",
  fuel_vendor: "/vendor-onboarding",
  credit_topup: "/credit-topup-request",
  subscription_payment: "/subscription-payment-request",
};

const getRouteForType = (type) => ROUTES_BY_TYPE[type] || null;

const formatType = (type) =>
  type
    ? type
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "";

const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(/ /g, "-");
};

const formatDateTime = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  const datePart = formatDate(isoDate);
  const timePart = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${datePart} ${timePart}`;
};

const mapTransactionAdjustment = (item, index) => ({
  key: String(item.id ?? index),
  id: item.id,
  date: formatDateTime(item.created_at),
  adjId: item.adj_id || "-",
  txnId: item.txn_id || "-",
  fleetCompany: item.fleet_company_name || "-",
  vehicleId: item.vehicle_id || "-",
  type: formatType(item.type) || "-",
  rawType: item.type,
  originalAmount: parseFloat(item.original_amount) || 0,
  netDiff: parseFloat(item.net_diff) || 0,
  status: item.status ? formatType(item.status) : "-",
});

const getInitials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return `${first}${last}`.toUpperCase();
};

const mapApprovalRequest = (item, index) => {
  const submittedBy = item.submitted_by_name || "";
  const initials = getInitials(submittedBy);

  return {
    key: String(item.id ?? index),
    submittedBy,
    initials,
    avatar: resolveAvatarUrl(item.submitted_by_avatar),
    color: AVATAR_COLORS[index % AVATAR_COLORS.length],
    id: item.id,
    requestId: item.request_id || "-",
    entity: item.legal_name || "",
    type: item.type || "",
    rawType: item.type,
    attachments:
      item.payment_method === "wipay"
        ? "Paid via WiPay"
        : `${item.document_count ?? 0} Files`,
    date: formatDate(item.submitted_at),
    status: item.status || null,
  };
};

const ApprovalsRequests = () => {
  const { canEdit } = usePageAccess("approvals-requests");
  const navigate = useNavigate();

  const handleRowClick = (record) => {
    const route = getRouteForType(record.rawType);
    if (route) {
      navigate(route, { state: { id: record.id } });
    } else {
      console.log("No specific route for this type:", record.rawType);
    }
  };

  const [approvalReqData, setApprovalReqData] = useState([]);
  const [requestCounts, setRequestCounts] = useState({
    fleet_requests: 0,
    vendor_requests: 0,
    credit_topup_requests: 0,
    subscription_payment_requests: 0,
  });
  const [approvedTodayCount, setApprovedTodayCount] = useState(0);

  const fetchApprovalRequests = async () => {
    try {
      const res = await getApprovalsRequests();
      const results = res?.data?.data?.results || [];

      if (Math.floor(res?.status / 100) === 2 && Array.isArray(results)) {
        setApprovalReqData(results.map(mapApprovalRequest));
        setTransactionData(
          Array.isArray(res?.data?.data?.transaction_adjustments)
            ? res.data.data.transaction_adjustments.map(
                mapTransactionAdjustment,
              )
            : [],
        );
        setRequestCounts({
          fleet_requests: res?.data?.data?.counts?.fleet_requests ?? 0,
          vendor_requests: res?.data?.data?.counts?.vendor_requests ?? 0,
          credit_topup_requests:
            res?.data?.data?.counts?.credit_topup_requests ?? 0,
          subscription_payment_requests:
            res?.data?.data?.counts?.subscription_payment_requests ?? 0,
        });

        const today = new Date().toDateString();
        setApprovedTodayCount(
          results.filter(
            (item) =>
              item.status === "approved" &&
              item.submitted_at &&
              new Date(item.submitted_at).toDateString() === today,
          ).length,
        );
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to load approval requests."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to load approval requests.",
        ),
      );
    }
  };

  useEffect(() => {
    fetchApprovalRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTxnStatusChange = async (e, id, status) => {
    e.stopPropagation();

    try {
      const res = await updateTransactionAdjustmentStatus(id, status);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          res?.data?.message || `Adjustment ${status} successfully!`,
        );
        fetchApprovalRequests();
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to update adjustment status."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to update adjustment status.",
        ),
      );
    }
  };

  const pendingCount = approvalReqData.filter(
    (item) => (item.status || "pending").toLowerCase() === "pending",
  ).length;

  const uniqueTypes = [...new Set(approvalReqData.map((item) => item.type))];

  const typeFilters = uniqueTypes.map((t) => ({
    text: formatType(t),
    value: t,
  }));

  const getAttachmentCount = (val) =>
    parseInt(val?.replace(/[^0-9]/g, "") || "0", 10);

  const attachmentFilters = [
    ...new Set(approvalReqData.map((item) => item.attachments)),
  ]
    .filter(Boolean)
    .sort((a, b) => getAttachmentCount(a) - getAttachmentCount(b))
    .map((value) => ({ text: value, value }));

  const statusFilters = [
    ...new Set(approvalReqData.map((item) => item.status || "pending")),
  ].map((value) => ({ text: formatType(value), value }));

  const approvalReqColumns = [
    {
      title: "Submitted By",
      dataIndex: "submittedBy",
      key: "submittedBy",
      render: (text, record) => {
        if (!text) return "-";
        const initials = getInitials(text);

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {record.avatar ? (
              <img
                src={record.avatar}
                alt={text}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div className={`vendor-avatar avatar-${record.color || "blue"}`}>
                {initials}
              </div>
            )}
            <span>{text}</span>
          </div>
        );
      },
      enableSearch: true,
    },
    {
      title: "ID",
      dataIndex: "requestId",
      key: "requestId",
      enableSearch: true,
    },
    {
      title: "Entity",
      dataIndex: "entity",
      key: "entity",
      enableSearch: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (typeValue) => {
        const typeArray = typeValue ? [].concat(typeValue) : [];

        return (
          <div>
            {typeArray.map((t) => (
              <span key={t} className="type-badge">
                {formatType(t)}
              </span>
            ))}
          </div>
        );
      },

      filters: typeFilters,
      onFilter: (value, record) => record.type?.includes(value),
    },
    {
      title: "Attachments",
      dataIndex: "attachments",
      key: "attachments",
      render: (text) => (
        <span className="attachment-badge">{text || "0 Files"}</span>
      ),
      sorter: (a, b) =>
        getAttachmentCount(a.attachments) - getAttachmentCount(b.attachments),
      filters: attachmentFilters,
      onFilter: (value, record) => record.attachments === value,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
    },
    {
      title: (
        <span className="status-title-with-count">
          {pendingCount > 0 && (
            <span className="status-count-badge">
              {pendingCount > 99 ? "99+" : pendingCount}
            </span>
          )}
          Status
        </span>
      ),
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const normalized = (status || "pending").toLowerCase();
        const cls =
          normalized === "approved"
            ? "status-approved"
            : normalized === "denied" || normalized === "rejected"
              ? "status-denied"
              : normalized === "revision_requested"
                ? "status-revision-requested"
                : "status-pending";
        return (
          <span className={`status-badge ${cls}`}>
            {formatType(status || "pending")}
          </span>
        );
      },
      filters: statusFilters,
      onFilter: (value, record) => (record.status || "pending") === value,
    },
    {
      title: "Action",
      dataIndex: "status",
      key: "action",
      render: (status, record) => (
        <Button
          type="text"
          icon={<EyeOutlined style={{ fontSize: 18, color: "#4F46E5" }} />}
          onClick={() => handleRowClick(record)}
        />
      ),
    },
  ];

  const [transactionData, setTransactionData] = useState([]);

  const uniqueTxnTypes = [
    ...new Set(transactionData.map((item) => item.rawType)),
  ];
  const txnTypeFilters = uniqueTxnTypes.map((t) => ({
    text: formatType(t),
    value: t,
  }));

  const txnStatusFilters = [
    ...new Set(transactionData.map((item) => item.status)),
  ]
    .filter((value) => value && value !== "-")
    .map((value) => ({ text: value, value }));

  const transactionColumns = [
    {
      title: "Date/Time",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      render: (text) => {
        if (!text) return "-";
        const parts = text.split(" ");
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: "1.2",
            }}
          >
            <span>{parts[0]}</span>
            <span style={{ color: "#71717A" }}>{parts[1]}</span>
          </div>
        );
      },
    },
    {
      title: "Adjustment ID",
      dataIndex: "adjId",
      key: "adjId",
      enableSearch: true,
    },
    {
      title: "Transaction ID",
      dataIndex: "txnId",
      key: "txnId",
      enableSearch: true,
    },
    {
      title: "Fleet Company",
      dataIndex: "fleetCompany",
      key: "fleetCompany",
      enableSearch: true,
    },
    {
      title: "Vehicle ID",
      dataIndex: "vehicleId",
      key: "vehicleId",
      enableSearch: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filters: txnTypeFilters,
      onFilter: (value, record) => record.rawType === value,
      render: (type, record) => (
        <span className={`type-badge badge-${record.rawType?.toLowerCase()}`}>
          {type}
        </span>
      ),
    },
    {
      title: "Original Amount",
      dataIndex: "originalAmount",
      key: "originalAmount",
      sorter: (a, b) => a.originalAmount - b.originalAmount,
      render: (val) => `$${val.toFixed(2)}`,
    },
    {
      title: "Net Diff",
      dataIndex: "netDiff",
      key: "netDiff",
      sorter: (a, b) => a.netDiff - b.netDiff,
      render: (val) => {
        const isNegative = val < 0;
        return (
          <span
            style={{
              color: isNegative ? "#ef4444" : "#22c55e",
              fontWeight: "500",
            }}
          >
            {isNegative ? "-" : "+"}${Math.abs(val).toFixed(2)}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: txnStatusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status, record) =>
        !canEdit ? (
          <span
            className={`status-badge ${
              status === "Denied"
                ? "denied"
                : status === "Approved"
                  ? "approved"
                  : ""
            }`}
          >
            {status || "-"}
          </span>
        ) : (
          <div
            className="action-buttons-container"
            style={{ display: "flex", gap: "10px" }}
          >
            <button
              type="button"
              className={`status-badge ${status === "Denied" ? "denied" : ""}`}
              style={{ cursor: "pointer", border: "none" }}
              onClick={(e) => handleTxnStatusChange(e, record.id, "denied")}
            >
              Denied
            </button>
            <button
              type="button"
              className={`status-badge ${status === "Approved" ? "approved" : ""}`}
              style={{ cursor: "pointer", border: "none" }}
              onClick={(e) => handleTxnStatusChange(e, record.id, "approved")}
            >
              Approved
            </button>
          </div>
        ),
    },
  ];

  return (
    <div className="approvals">
      <div className="row">
        <div className="tabs">
          <GenericStatCard
            title="Fleet Requests"
            value={countValue(requestCounts.fleet_requests) ?? 0}
            {...(requestCounts.fleet_requests?.percentage != null && {
              percentage: `${requestCounts.fleet_requests.percentage}`,
            })}
            isIncrease={requestCounts.fleet_requests?.is_increase}
            icon={fleetIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Vendor Requests"
            value={countValue(requestCounts.vendor_requests) ?? 0}
            {...(requestCounts.vendor_requests?.percentage != null && {
              percentage: `${requestCounts.vendor_requests.percentage}`,
            })}
            isIncrease={requestCounts.vendor_requests?.is_increase}
            icon={vendorIcon}
          />
        </div>

        <div className="tabs">
          <GenericStatCard
            title="Credit Request"
            value={countValue(requestCounts.credit_topup_requests) ?? 0}
            {...(requestCounts.credit_topup_requests?.percentage != null && {
              percentage: `${requestCounts.credit_topup_requests.percentage}`,
            })}
            isIncrease={requestCounts.credit_topup_requests?.is_increase}
            icon={creditIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Approved Today"
            value={approvedTodayCount}
            icon={approvedIcon}
          />
        </div>
      </div>
      <div className="approval-table">
        <GenericTable
          title="Approvals & Request"
          dataSource={approvalReqData}
          columns={approvalReqColumns}
          enableRowSelection={false}
          onSelectionChange={(keys, rows) => console.log(keys, rows)}
        />
      </div>
      <div>
        <GenericTable
          title="Transaction Adjustment"
          dataSource={transactionData}
          columns={transactionColumns}
          enableRowSelection={false}
        />
      </div>
    </div>
  );
};

export default ApprovalsRequests;
